"use client";

import Link from "next/link";
import { useEffect, useId, useRef, useState, useTransition, type FormEvent, type KeyboardEvent } from "react";
import { useRouter } from "next/navigation";

import type { LuoghiDelMenu } from "@/components/rullo/registro/RicercaRegistro";
import { FUOCO } from "@/components/ui/pulsanti";
import { categoriaGeografica, faccetteCategorie } from "@/lib/categorie";
import { PERIODI, PERIODO_LABEL, PRIORITY_MAP, RITARDO_FILTRI } from "@/lib/constants";
import {
  SENZA_FILTRI,
  FILONI,
  etichettaIntervallo,
  isFilone,
  filtriAttivi,
  hrefRullo,
  it,
  nomeTipo,
  normalizzaTipi,
  soloStorico,
  type Filone,
  type StatoRullo,
} from "@/lib/rullo";
import type { Suggeribili, VoceLuogo } from "@/lib/ricerca-pagina";
import type { Priority } from "@/lib/types";

/* Le costanti e la logica dei menu sono quelle di <RicercaRegistro>
   (components/rullo/registro), che resta per /rullo: qui cambia la veste,
   quella del modello di /ricerca del 20/10/2026 (`uploads/ricerca.html`,
   `.rc-cerca`), e si aggiungono i suggerimenti sotto il campo. */
const RUBRICA = "rubrica:";
const PIU_TIPI = "__piu__";
/** Le voci del gruppo «Notizie, casi e report» del menu dei tipi (era
 *  «Filoni», con i soli casi e report, fino al 25/10/2026): `?tipo=notizia`,
 *  `?tipo=caso` e `?tipo=report`, non un `?priority=`. */
const FILONE = "filone:";
const NOME_FILONE: Record<Filone, string> = { notizia: "Notizie", caso: "Casi", report: "Report" };
const ENTITA_ETICHETTE: Record<string, string> = {
  persons: "Persona",
  companies: "Azienda",
  institutions: "Istituzione",
  organizations: "Organizzazione",
  parties: "Partito",
  media: "Media",
  places: "Luogo",
  sectors: "Settore",
  figures: "Indice",
  tech: "Tecnologia",
};

function chiave(s: string): string {
  return s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
}

type Suggerimento = { tipo: string; nome: string; nota: string; href: string };

/** I suggerimenti per il testo scritto (`.rc-sug` del modello): il paese
 *  (la sua scheda), lo stesso nome come luogo (il filtro per voce), i nodi
 *  dell'atlante che lo nominano. Al più sei righe, da due lettere. */
function suggerimenti(t: string, s: Suggeribili, stato: StatoRullo, voce: VoceLuogo | null): Suggerimento[] {
  const k = chiave(t);
  if (k.length < 2) return [];
  const out: Suggerimento[] = [];
  const paesi = s.paesi.filter((p) => chiave(p.nome).startsWith(k)).slice(0, 2);
  for (const p of paesi) {
    out.push({ tipo: "Paese", nome: p.nome, nota: p.area ? `scheda Paese · ${p.area}` : "scheda Paese", href: `/paesi/${p.slug}` });
  }
  for (const p of paesi.slice(0, 1)) {
    const quanti = voce && chiave(voce.nome) === chiave(p.nome) ? `${it(voce.quanti)} dispacci` : "come luogo";
    out.push({
      tipo: "Luogo",
      nome: `${p.nome} come luogo`,
      nota: quanti,
      href: hrefRullo(stato, { q: "", etype: "places", entity: p.nome }),
    });
  }
  for (const n of s.nodi) {
    if (out.length >= 6) break;
    if (chiave(n.nome).includes(k) || (k.length >= 3 && chiave(n.short).includes(k))) {
      out.push({ tipo: "Nodo", nome: n.nome, nota: n.short, href: `/corridoi/${n.slug}` });
    }
  }
  return out.slice(0, 6);
}

/**
 * Il riquadro di ricerca di /ricerca (`.rc-cerca` del modello del
 * 20/10/2026): bordo inchiostro con il filetto rame in cima, il campo
 * Gelasio 22 con «Cerca ↗», i suggerimenti che si aprono scrivendo, i
 * quattro menu e la riga dei filtri accesi con l'azzeramento.
 *
 * Lo stato vero è nell'indirizzo (come in <RicercaRegistro>): il
 * riquadro ne tiene uno specchio che si riallinea quando la navigazione
 * atterra. Senza JavaScript è un `<form method="get">`.
 */
export function CercaArchivio({
  stato,
  luoghi,
  categorie,
  suggeribili,
  voce,
}: {
  stato: StatoRullo;
  luoghi: LuoghiDelMenu;
  categorie: { name: string; count: number }[];
  suggeribili: Suggeribili;
  /** La voce `wiki` della parola cercata, se c'è: dà il numero «come
   *  luogo» nella riga dei suggerimenti. */
  voce: VoceLuogo | null;
}) {
  const router = useRouter();
  const [inCorso, startTransition] = useTransition();
  const id = useId();
  const campo = useRef<HTMLInputElement>(null);

  const [mostrato, setMostrato] = useState(stato);
  const [testo, setTesto] = useState(stato.q);
  const [statoPrec, setStatoPrec] = useState(stato);
  if (stato !== statoPrec) {
    setStatoPrec(stato);
    setMostrato(stato);
    setTesto(stato.q);
  }

  const attesa = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (attesa.current) clearTimeout(attesa.current);
    },
    []
  );

  function vai(cambi: Partial<StatoRullo>, subito = false) {
    const c = cambi.tipi
      ? { ...cambi, tipi: normalizzaTipi(cambi.tipi, mostrato.vista.tipi, mostrato.vista.tipiEspliciti) }
      : cambi;
    setMostrato({ ...mostrato, ...c, page: 1 });
    const href = hrefRullo(mostrato, c);
    if (attesa.current) clearTimeout(attesa.current);
    const parti = () =>
      startTransition(() => {
        router.push(href, { scroll: false });
      });
    if (subito) parti();
    else attesa.current = setTimeout(parti, RITARDO_FILTRI);
  }

  function cerca(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    vai({ q: testo.trim() }, true);
  }

  /* Le categorie divise in temi e rubriche geografiche (lib/categorie.ts):
     le rubriche vanno nel menu del luogo, come in <RicercaRullo>. */
  const { temi, aree: tutteLeRubriche } = faccetteCategorie(categorie);
  /* Una rubrica che ha lo stesso nome di un'area (Africa, Asia, Europa,
     Medio Oriente) non si ripete: la voce fra le Aree le comprende tutte e
     due, perché a monte quel luogo si cerca come area oppure come rubrica
     (`domanda` in RisultatiRullo, la stessa di /ricerca). Restano qui solo le rubriche
     che non sono anche un'area. */
  const nomiLuoghi = new Set([...luoghi.paesi, ...luoghi.aree].map((n) => n.toLowerCase()));
  const rubriche = tutteLeRubriche.filter((c) => !nomiLuoghi.has(c.name.toLowerCase()));
  const catGeografica = categoriaGeografica(mostrato.cat);
  const valoreLuogo = mostrato.paese || (catGeografica ? RUBRICA + mostrato.cat : "");
  const valoreCategoria = catGeografica ? "" : mostrato.cat;
  const inArea = (luogo: string) => luoghi.aree.includes(luogo) || categoriaGeografica(luogo);

  function scegliLuogo(v: string) {
    if (v.startsWith(RUBRICA)) vai({ paese: "", cat: v.slice(RUBRICA.length) });
    else vai({ paese: v, cat: catGeografica ? "" : mostrato.cat });
  }

  /* Un luogo o una categoria che arriva dall'indirizzo (un riferimento
     cliccato, un link) ma che il menu non elenca: si aggiunge in testa,
     così il menu dice la verità invece di tornare su «Tutti». */
  const luogoFuoriMenu =
    mostrato.paese && !luoghi.paesi.includes(mostrato.paese) && !luoghi.aree.includes(mostrato.paese)
      ? mostrato.paese
      : "";
  const categoriaFuoriMenu =
    valoreCategoria && !temi.some((c) => c.name === valoreCategoria) ? valoreCategoria : "";
  const rubricaFuoriMenu =
    catGeografica && !rubriche.some((c) => c.name === mostrato.cat) ? mostrato.cat : "";

  /* I tipi in due gruppi, sempre gli stessi due: le pubblicazioni
     correnti e lo storico. Sul Rullo lo storico è un tipo che si sceglie
     solo esplicitamente (`tipiEspliciti`); su /ricerca è un tipo della
     vista come gli altri, e «Tutti i tipi» lo comprende. Il menu è lo
     stesso, cambia che cosa vuol dire la voce vuota. */
  /* Il tipo a monte «Report» non si offre fra le pubblicazioni correnti:
     accanto a «Report» dei Filoni sarebbero due voci con lo stesso nome e
     due liste diverse (a monte «Report» sono anche filoni da tre voci, cioè
     casi per la regola della redazione). Resta solo se l'indirizzo lo chiede
     (`?priority=report`, un link vecchio), così il menu non mente. */
  /* Lo stesso per il tipo a monte «Notizie» dal 25/10/2026: nel gruppo
     in testa c'è «Notizie» come la scrivono le righe (tutto quello che non è
     un caso o un report, `?tipo=notizia`), e due «Notizie» con elenchi
     diversi (217 dispacci a monte contro decine di migliaia) direbbero due
     cose. Resta solo se l'indirizzo chiede `?priority=notizie`. */
  const tipiCorrenti = mostrato.vista.tipi.filter(
    (t) =>
      t !== "archivio" &&
      (t !== "report" || mostrato.tipi.includes("report")) &&
      (t !== "notizie" || mostrato.tipi.includes("notizie"))
  );
  const tipiStorici = [
    ...(mostrato.vista.tipiEspliciti ?? []),
    ...mostrato.vista.tipi.filter((t) => t === "archivio"),
  ];
  /* «Casi» e «Report» sono filoni, non tipi a monte (`?tipo=`, non
     `?priority=`): sceglierne uno azzera i tipi, e scegliere un tipo lo
     toglie, così il menu dice sempre una cosa sola. */
  const valoreTipo = mostrato.filone
    ? FILONE + mostrato.filone
    : mostrato.tipi.length === 0
      ? ""
      : mostrato.tipi.length === 1
        ? mostrato.tipi[0]!
        : PIU_TIPI;
  const storico = soloStorico(mostrato);

  /* Le etichette dei filtri accesi. Il testo cercato è la prima. */
  type Chip = { chiave: string; testo: string; cambi: Partial<StatoRullo> };
  const chip: Chip[] = [
    ...(mostrato.q ? [{ chiave: "q", testo: `«${mostrato.q}»`, cambi: { q: "" } }] : []),
    ...(mostrato.paese
      ? [{ chiave: "paese", testo: `${inArea(mostrato.paese) ? "Area" : "Paese"}: ${mostrato.paese}`, cambi: { paese: "" } }]
      : []),
    ...(mostrato.cat
      ? [{ chiave: "cat", testo: `${catGeografica ? "Area" : "Categoria"}: ${mostrato.cat}`, cambi: { cat: "" } }]
      : []),
    ...(mostrato.filone
      ? [{ chiave: "filone", testo: NOME_FILONE[mostrato.filone], cambi: { filone: "" as const } }]
      : []),
    ...mostrato.tipi.map((t) => ({
      chiave: `tipo-${t}`,
      testo: t === "archivio" ? "Archivio storico" : `Tipo: ${nomeTipo(PRIORITY_MAP[t].label)}`,
      cambi: { tipi: mostrato.tipi.filter((x) => x !== t) },
    })),
    ...(mostrato.date
      ? [{ chiave: "date", testo: `Periodo: ${PERIODO_LABEL[mostrato.date]}`, cambi: { date: "" as const } }]
      : []),
    /* L'intervallo `?from=`/`?before=`, solo su /ricerca: ci atterrano i
       link per anno di /archivio («Anno 2015»), e si toglie da qui. Sul
       Rullo la vista non lo legge e resta sempre vuoto. */
    ...(mostrato.da || mostrato.fino
      ? [{ chiave: "intervallo", testo: etichettaIntervallo(mostrato), cambi: { da: "", fino: "" } }]
      : []),
    ...(mostrato.entity
      ? [
          {
            chiave: "entity",
            testo: `${ENTITA_ETICHETTE[mostrato.etype] ?? "Riferimento"}: ${mostrato.entity}`,
            cambi: { etype: "", entity: "" },
          },
        ]
      : []),
  ];
  const qualcosa = filtriAttivi(mostrato) > 0 || Boolean(mostrato.q);

  const [aperto, setAperto] = useState(false);
  const [attivo, setAttivo] = useState(-1);
  const sugg = aperto ? suggerimenti(testo, suggeribili, mostrato, voce) : [];
  const idLista = `${id}-sug`;

  function tasto(e: KeyboardEvent<HTMLInputElement>) {
    if (!sugg.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setAttivo((a) => Math.min(a + 1, sugg.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setAttivo((a) => Math.max(a - 1, -1));
    } else if (e.key === "Escape") {
      setAperto(false);
      setAttivo(-1);
    } else if (e.key === "Enter" && attivo >= 0) {
      e.preventDefault();
      setAperto(false);
      router.push(sugg[attivo]!.href);
    }
  }

  const etichetta = "flex flex-col gap-2 font-sans text-voce font-semibold text-sommesso";
  const menu =
    "appearance-auto rounded-none border border-rule bg-bg px-3 py-[11px] font-sans text-[14.5px] font-normal text-ink " + FUOCO;

  return (
    <div id="rullo-ricerca" className="scroll-mt-4">
      <form
        role="search"
        action={mostrato.vista.percorso}
        method="get"
        onSubmit={(e) => {
          setAperto(false);
          cerca(e);
        }}
        className="relative z-[2] mt-7 border border-t-4 border-ink border-t-segnale bg-surface px-6 pb-4 pt-[22px] max-[520px]:px-4"
      >
        {mostrato.da ? <input type="hidden" name="from" value={mostrato.da} /> : null}
        {mostrato.fino ? <input type="hidden" name="before" value={mostrato.fino} /> : null}
        <label htmlFor={`${id}-q`} className="mb-2 block font-sans text-voce font-semibold text-sommesso">
          Cerca nel rullo
        </label>
        <div className="relative">
          <div className="flex border border-ink bg-bg">
            <input
              ref={campo}
              id={`${id}-q`}
              name="q"
              type="search"
              value={testo}
              onChange={(e) => {
                setTesto(e.target.value);
                setAperto(true);
                setAttivo(-1);
              }}
              onFocus={() => setAperto(true)}
              onBlur={() => setTimeout(() => setAperto(false), 150)}
              onKeyDown={tasto}
              placeholder="Una notizia, un Paese, un’impresa, un nodo…"
              autoComplete="off"
              role="combobox"
              aria-expanded={sugg.length > 0}
              aria-controls={idLista}
              aria-activedescendant={attivo >= 0 ? `${idLista}-${attivo}` : undefined}
              className="min-w-0 flex-1 border-0 bg-transparent px-4 py-3.5 font-serif text-[22px] text-ink outline-none placeholder:text-sommesso max-[520px]:text-[18px]"
            />
            <button
              type="submit"
              aria-busy={inCorso}
              className={`min-w-[112px] cursor-pointer border-0 bg-mare px-[26px] font-sans text-[15px] font-semibold text-suMare ${FUOCO}`}
            >
              {/* Mentre la nuova pagina arriva il pulsante lo dice: una ricerca
                  nuova a monte costa qualche secondo, e senza un segno sembrava
                  che «Cerca» non facesse niente. */}
              {inCorso ? "Cerco…" : <>Cerca <span aria-hidden>↗</span></>}
            </button>
          </div>
          {sugg.length ? (
            <ul
              id={idLista}
              role="listbox"
              className="absolute left-0 right-[150px] top-full z-[3] m-0 list-none border border-t-0 border-ink bg-surface p-0 py-1.5 shadow-[0_10px_24px_rgba(0,0,0,0.12)] max-[900px]:right-0"
            >
              <li aria-hidden className="px-4 pb-1 pt-1.5 font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-sommesso">
                Suggerimenti
              </li>
              {sugg.map((x, i) => (
                <li key={x.tipo + x.nome} id={`${idLista}-${i}`} role="option" aria-selected={i === attivo}>
                  <Link
                    href={x.href}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setAperto(false)}
                    className={`grid grid-cols-[70px_minmax(0,1fr)_auto] items-baseline gap-3 px-4 py-2 text-ink no-underline hover:bg-petrolio max-[900px]:grid-cols-[56px_minmax(0,1fr)] ${
                      i === attivo ? "bg-petrolio" : ""
                    }`}
                  >
                    <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-segnale">{x.tipo}</span>
                    <b className="font-serif text-[17px] font-normal">{x.nome}</b>
                    <small className="font-sans text-[12.5px] text-sommesso max-[900px]:col-start-2">{x.nota}</small>
                  </Link>
                </li>
              ))}
              <li
                aria-hidden
                className="mt-1 flex items-center justify-between border-t border-rule px-4 pb-1 pt-2 font-sans text-[12.5px] text-sommesso"
              >
                <span>Invio per cercare «{testo.trim()}» in tutto l’archivio</span>
                <kbd className="border border-rule px-1.5 font-mono text-[11px] font-semibold">↵</kbd>
              </li>
            </ul>
          ) : null}
        </div>

        <div className="mt-4 grid grid-cols-4 gap-3.5 max-[900px]:grid-cols-1">
          <label className={etichetta}>
            Paese o area
            <select name="place" value={valoreLuogo} onChange={(e) => scegliLuogo(e.target.value)} className={menu}>
              <option value="">Tutti i Paesi e le aree</option>
              {luogoFuoriMenu ? <option value={luogoFuoriMenu}>{luogoFuoriMenu}</option> : null}
              {rubricaFuoriMenu ? <option value={RUBRICA + rubricaFuoriMenu}>{rubricaFuoriMenu}</option> : null}
              {luoghi.paesi.length ? (
                <optgroup label="Paesi">
                  {luoghi.paesi.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </optgroup>
              ) : null}
              {luoghi.aree.length ? (
                <optgroup label="Aree">
                  {luoghi.aree.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </optgroup>
              ) : null}
              {rubriche.length ? (
                <optgroup label="Altre rubriche geografiche">
                  {rubriche.map((c) => (
                    <option key={c.name} value={RUBRICA + c.name}>
                      {c.name}
                    </option>
                  ))}
                </optgroup>
              ) : null}
            </select>
          </label>
          <label className={etichetta}>
            Categoria
            <select name="cat" value={valoreCategoria} onChange={(e) => vai({ cat: e.target.value })} className={menu}>
              <option value="">Tutte le categorie</option>
              {categoriaFuoriMenu ? <option value={categoriaFuoriMenu}>{categoriaFuoriMenu}</option> : null}
              {temi.map((c) => (
                <option key={c.name} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className={etichetta}>
            Tipo di dispaccio
            <select
              name="priority"
              value={valoreTipo}
              onChange={(e) => {
                const v = e.target.value;
                if (v === PIU_TIPI) return;
                const f = v.startsWith(FILONE) ? v.slice(FILONE.length) : "";
                if (isFilone(f)) vai({ filone: f, tipi: [] });
                else vai({ filone: "", tipi: v ? [v as Priority] : [] });
              }}
              className={menu}
            >
              <option value="">Tutti i tipi</option>
              {valoreTipo === PIU_TIPI ? (
                <option value={PIU_TIPI} disabled>
                  {mostrato.tipi.length} tipi scelti
                </option>
              ) : null}
              <optgroup label="Notizie, casi e report">
                {FILONI.map((f) => (
                  <option key={f} value={FILONE + f}>
                    {NOME_FILONE[f]}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Pubblicazioni correnti">
                {tipiCorrenti.map((t) => (
                  <option key={t} value={t}>
                    {nomeTipo(PRIORITY_MAP[t].label)}
                  </option>
                ))}
              </optgroup>
              {tipiStorici.length ? (
                <optgroup label="Storico">
                  {tipiStorici.map((t) => (
                    <option key={t} value={t}>
                      {t === "archivio" ? "Archivio storico" : nomeTipo(PRIORITY_MAP[t].label)}
                    </option>
                  ))}
                </optgroup>
              ) : null}
            </select>
          </label>
          <label className={etichetta}>
            Periodo
            <select
              name="date"
              value={mostrato.date}
              onChange={(e) => vai({ date: e.target.value as StatoRullo["date"], da: "", fino: "" })}
              className={menu}
            >
              <option value="">Qualsiasi data</option>
              {PERIODI.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        {/* La riga del piede: i filtri accesi come etichette piene
            rimovibili, la nota sulla ricerca, l'azzeramento a destra. */}
        <div className="mt-3.5 flex flex-wrap items-center gap-x-[18px] gap-y-2.5 border-t border-rule pt-3">
          {chip.length ? (
            <ul aria-label="Filtri attivi" className="m-0 flex list-none flex-wrap gap-2 p-0">
              {chip.map((c) => (
                <li key={c.chiave}>
                  <span className="inline-flex items-center gap-2 bg-mare py-[5px] pl-3 pr-1.5 font-mono text-meta font-semibold uppercase tracking-[0.08em] text-suMare">
                    {c.testo}
                    <button
                      type="button"
                      aria-label={`Rimuovi filtro ${c.testo}`}
                      onClick={() => {
                        if (c.chiave === "q") setTesto("");
                        vai(c.cambi, true);
                      }}
                      className={`cursor-pointer border-0 bg-transparent px-1 text-[16px] leading-none text-suMare ${FUOCO}`}
                    >
                      ×
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
          <span className="font-sans text-[13px] text-sommesso">
            {storico
              ? "Stai cercando nell’archivio storico: dispacci degli anni passati, che non si aggiornano più."
              : "Ricerca su titolo, sommario e categoria · più recenti prima"}
          </span>
          <button
            type="button"
            disabled={!qualcosa}
            onClick={() => {
              setTesto("");
              vai({ ...SENZA_FILTRI, q: "" }, true);
            }}
            className={`ml-auto cursor-pointer border-0 bg-transparent p-0 font-sans text-corpo font-semibold text-mare hover:underline hover:underline-offset-4 disabled:cursor-default disabled:opacity-55 disabled:hover:no-underline max-[900px]:ml-0 ${FUOCO}`}
          >
            Azzera filtri ↺
          </button>
        </div>
      </form>
    </div>
  );
}
